import { ENDPOINT_NAMES } from '../../../constants';
import type { SurveyConfigBlock, SurveyConfigBlockGroup } from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch, RootState } from '../../types';
import { findMatchingCachedQueries, OptimisticUpdater } from '../../utils';

interface PatchBlockIdsArgs {
  surveyConfigId: string;
  surveyConfigBlockGroupId: string;
  body: string[];
}

interface PatchBlockIdsContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: SurveyConfigBlockGroup }>;
  getState: () => RootState;
}

/**
 * ### updateBlockIdsInCache
 *
 * @description
 * Updates the blockIds array for a specific block group in a draft array.
 *
 * @example
 * updateBlockIdsInCache(draft, 'group-123', ['block-1', 'block-2']);
 *
 * @param draft - The draft array containing survey config block groups
 * @param blockGroupId - The ID of the block group to update
 * @param newBlockIds - The new array of block IDs
 */
const updateBlockIdsInCache = (
  draft: SurveyConfigBlockGroup[] | undefined,
  blockGroupId: string,
  newBlockIds: string[],
): void => {
  if (!draft) return;

  const blockGroup = draft.find((group) => group.id === blockGroupId);
  if (blockGroup) {
    blockGroup.surveyConfigBlockIds = newBlockIds;
  }
};

/**
 * ### updateBlockGroupIdInBlocks
 *
 * @description
 * Updates the surveyConfigBlockGroupId field in blocks based on the new blockIds array.
 * Blocks in the new array get the group id, blocks that were previously in the group
 * but are not in the new array get their group id set to null.
 *
 * @example
 * updateBlockGroupIdInBlocks(draft, 'group-123', ['block-1', 'block-2']);
 *
 * @param draft - The draft array containing survey config blocks
 * @param blockGroupId - The ID of the block group
 * @param newBlockIds - The new array of block IDs that should be in this group
 */
const updateBlockGroupIdInBlocks = (
  draft: SurveyConfigBlock[] | undefined,
  blockGroupId: string,
  newBlockIds: string[],
): void => {
  if (!draft) return;

  const newBlockIdsSet = new Set(newBlockIds);

  draft.forEach((block) => {
    const blockId = block.id;
    if (!blockId) return;

    if (newBlockIdsSet.has(blockId)) {
      // Block is in the new array, set it to this group
      block.surveyConfigBlockGroupId = blockGroupId;
    } else if (block.surveyConfigBlockGroupId === blockGroupId) {
      // Block was previously in this group but is not in the new array, remove it
      block.surveyConfigBlockGroupId = null;
    }
  });
};

/**
 * ### Patch Block Group BlockIds Enhancer
 *
 * @description
 * Enhancer for patchSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdBlockIds endpoint.
 * Optimistically updates the blockIds array in the cache before the server responds.
 * If the request fails, the update is rolled back.
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const patchSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdBlockIds =
  (api: ProjectApi) => ({
    async onQueryStarted(args: PatchBlockIdsArgs, context: PatchBlockIdsContext) {
      const { surveyConfigId, surveyConfigBlockGroupId, body } = args;
      const { dispatch, queryFulfilled, getState } = context;

      const updater = new OptimisticUpdater({ dispatch, api });

      // Optimistically update the block groups list cache
      updater.update({
        endpointName: ENDPOINT_NAMES.GET_SURVEY_CONFIG_BLOCK_GROUPS,
        args: { surveyConfigId },
        updateFn: (draft: SurveyConfigBlockGroup[]) =>
          updateBlockIdsInCache(draft, surveyConfigBlockGroupId, body),
      });

      // Optimistically update the blocks cache with the new surveyConfigBlockGroupId
      // Only update if blocks are actually cached (performance optimization)
      const state = getState();
      const queries = state.projectApi?.queries;
      const matchingQueries = findMatchingCachedQueries<{
        projectId: number;
        surveyConfigId: string;
      }>({
        queries,
        endpointName:
          ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS_AND_SURVEY_CONFIG_ID_BLOCKS,
        matcher: (args) => args.surveyConfigId === surveyConfigId,
      });

      // Only update blocks if queries exist (skip if not loaded yet)
      matchingQueries.forEach((args) => {
        updater.update({
          endpointName:
            ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_SURVEY_CONFIGS_AND_SURVEY_CONFIG_ID_BLOCKS,
          args,
          updateFn: (draft: SurveyConfigBlock[]) =>
            updateBlockGroupIdInBlocks(draft, surveyConfigBlockGroupId, body),
        });
      });

      try {
        await queryFulfilled;
      } catch {
        // Rollback optimistic update on failure
        updater.rollback();
      }
    },
  });

export default patchSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdBlockIds;
